import React, { ForwardedRef, forwardRef } from "react";
import styled, { keyframes } from "styled-components";
import { InputLabel } from "../Typography";
import { ValidationError } from "./Input";
import TextareaAutosize, {
  TextareaAutosizeProps,
} from "react-textarea-autosize";

export const TextArea = styled(TextareaAutosize)<{ $resizeHandles: boolean }>`
  font-size: 16px;
  background-color: ${({ theme }) => theme.input.backgroundColor};
  color: ${({ theme }) => theme.input.color};
  border-width: ${({ theme }) => theme.input.border.width};
  border-style: solid;
  border-color: ${({ theme }) => theme.input.border.color};
  border-radius: ${({ theme }) => theme.input.border.radius};
  width: 100%;
  padding: 0.375rem 0.75rem;
  resize: ${(props) => (props.$resizeHandles ? "both" : "none")};

  &:focus {
    color: ${({ theme }) => theme.input.focus.color};
    background-color: ${({ theme }) => theme.input.focus.backgroundColor};
    border-color: ${({ theme }) => theme.input.focus.border.color};
    outline: 0;
  }

  &:hover {
    cursor: text;
  }
`;

export const WhiteTextArea = styled(TextArea)`
  background-color: white;
  border-color: ${({ theme }) => theme.input.focus.border.color};

  &:focus {
    background-color: white;
    border-color: ${({ theme }) => theme.input.focus.border.color};
  }
`;

const CharacterCounter = styled.span<{ $isMax: boolean }>`
  float: right;
  font-size: 80%;
  margin-right: 0.5rem;
  animation-name: ${({ $isMax }) => $isMax && shake};
  animation-duration: 100ms;
  animation-timing-function: linear;
  animation-iteration-count: 3;
`;
const shake = keyframes`
  0% {
    transform: translate(0%, 0);
  }
  25% {
    transform: translate(-0.5rem, 0);
  }
  50% {
    transform: translate(0, 0);
  }
  75% {
    transform: translate(0.5rem, 0);
  }
  100% {
    transform: translate(0, 0);
  }
`;

interface TextAreaWithLabelAndErrorProps extends TextareaAutosizeProps {
  label: string;
  whiteTextArea?: boolean;
  validationError?: string;
  resizeHandles?: boolean;
  handleChange: (text: string) => void;
  handleBlur?: (
    event: React.FocusEvent<HTMLTextAreaElement, HTMLTextAreaElement>
  ) => void;
}

export const TextAreaWithLabelAndError = forwardRef(
  (
    {
      value,
      maxLength,
      handleChange,
      handleBlur,
      label,
      required,
      id,
      validationError,
      whiteTextArea,
      resizeHandles = false,
      ...props
    }: TextAreaWithLabelAndErrorProps,
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    value = value || "";
    const truncatedValue = maxLength
      ? value.toString().slice(0, maxLength)
      : value.toString();
    const characterCount = truncatedValue.length;
    return (
      <>
        {label && (
          <InputLabel $isRequired={required} htmlFor={id}>
            {label}
          </InputLabel>
        )}
        {whiteTextArea ? (
          <WhiteTextArea
            id={id}
            value={truncatedValue}
            required={required}
            ref={ref}
            onBlur={handleBlur}
            onChange={({ target }) => handleChange(target.value)}
            maxLength={maxLength}
            $resizeHandles={resizeHandles}
            {...props}
          />
        ) : (
          <TextArea
            id={id}
            onBlur={handleBlur}
            onChange={({ target }) => handleChange(target.value)}
            value={truncatedValue}
            required={required}
            ref={ref}
            maxLength={maxLength}
            $resizeHandles={resizeHandles}
            {...props}
          />
        )}
        <CharacterCounter $isMax={characterCount === maxLength}>
          {maxLength && `${characterCount} / ${maxLength}`}
        </CharacterCounter>
        <ValidationError>{validationError && validationError}</ValidationError>
      </>
    );
  }
);
